import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { BookOpen, ChevronDown, FileText, Link, Loader2, Play, Video, X } from 'lucide-react';

import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import contentLibraryService, { type Video as ContentVideo } from '@/lib/api/contentLibrary';
import {
  getKnowledgeAssetTypeLabel,
  isDocumentKnowledgeAssetType,
} from '@/lib/api/knowledgeAssets';
import { cn } from '@/lib/utils';
import { useKnowledgeAssets } from '@/hooks/useKnowledgeAssets';

function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

const AssetIcon: React.FC<{ type: string; className?: string }> = ({ type, className }) => {
  if (isDocumentKnowledgeAssetType(type)) return <FileText className={className} />;
  if (type === 'video') return <Video className={className} />;
  return <Link className={className} />;
};

const VideoCard: React.FC<{
  video: ContentVideo;
  orgId: string;
  selected: boolean;
  onToggle: () => void;
}> = ({ video, orgId, selected, onToggle }) => {
  const { data: segmentsData } = useQuery({
    queryKey: ['video-segments-thumb', orgId, video.video_id],
    queryFn: async () => {
      const response = await contentLibraryService.getAllVideoSegments(video.video_id, orgId);
      if (!response.success || !response.data) return null;
      return response.data;
    },
    enabled: !!video.video_id,
    staleTime: 5 * 60 * 1000,
  });

  const thumbnailUrl = segmentsData?.segments?.[0]?.thumbnail_url ?? null;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onToggle}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onToggle();
        }
      }}
      className={cn(
        'relative overflow-hidden rounded-lg border transition-colors',
        selected
          ? 'border-primary ring-2 ring-primary/20'
          : 'border-border hover:border-muted-foreground/40',
      )}
    >
      <div className="relative flex aspect-video items-center justify-center bg-secondary">
        {thumbnailUrl ? (
          <img
            src={thumbnailUrl}
            alt={video.title || video.filename}
            className="h-full w-full object-cover"
          />
        ) : (
          <Play className="h-5 w-5 text-muted-foreground" />
        )}
        <div className="absolute left-2 top-2">
          <Checkbox checked={selected} tabIndex={-1} />
        </div>
        {video.duration_seconds != null && (
          <span className="absolute bottom-1.5 right-1.5 rounded bg-black/70 px-1.5 py-0.5 text-[10px] text-white">
            {formatDuration(video.duration_seconds)}
          </span>
        )}
      </div>
      <div className="px-2.5 py-2">
        <p className="truncate text-sm font-medium">{video.title || video.filename}</p>
        <p className="text-[11px] text-muted-foreground">
          {new Date(video.created_at).toLocaleDateString()}
        </p>
      </div>
    </div>
  );
};

export const ContentSelector: React.FC<{
  orgId: string;
  selectedVideoIds: string[];
  onSelectionChange: (ids: string[]) => void;
  disabled?: boolean;
  excludedVideoIds?: string[];
}> = ({
  orgId,
  selectedVideoIds,
  onSelectionChange,
  disabled = false,
  excludedVideoIds = [],
}) => {
  const [expanded, setExpanded] = useState(false);

  const { data: videosData, isLoading } = useQuery({
    queryKey: ['content-library-videos', orgId],
    queryFn: async () => {
      const response = await contentLibraryService.getOrganizationVideos(orgId);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to fetch videos');
      }
      return response.data;
    },
    enabled: !!orgId,
  });

  const readyVideos = useMemo(
    () =>
      (videosData?.videos ?? []).filter(
        (video) => video.status === 'ready' && !excludedVideoIds.includes(video.video_id)
      ),
    [excludedVideoIds, videosData?.videos]
  );
  const selectedVideos = readyVideos.filter((video) => selectedVideoIds.includes(video.video_id));

  const toggleVideo = (videoId: string) => {
    if (selectedVideoIds.includes(videoId)) {
      onSelectionChange(selectedVideoIds.filter((id) => id !== videoId));
    } else {
      onSelectionChange([...selectedVideoIds, videoId]);
    }
  };

  return (
    <div className="space-y-2">
      <Label className="flex items-center gap-1.5">
        <Video className="h-3.5 w-3.5" />
        Demo Assets
      </Label>

      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        disabled={disabled}
        className={cn(
          'flex w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background',
          'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50'
        )}
      >
        <span className="text-muted-foreground">
          {selectedVideoIds.length > 0
            ? `${selectedVideoIds.length} video${selectedVideoIds.length > 1 ? 's' : ''} selected`
            : 'Select demo videos...'}
        </span>
        <ChevronDown
          className={cn('h-4 w-4 opacity-50 transition-transform', expanded && 'rotate-180')}
        />
      </button>

      {expanded && (
        <div className="pt-1">
          {isLoading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          ) : readyVideos.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              No videos available in Content Library
            </p>
          ) : (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              {readyVideos.map((video) => (
                <VideoCard
                  key={video.video_id}
                  video={video}
                  orgId={orgId}
                  selected={selectedVideoIds.includes(video.video_id)}
                  onToggle={() => toggleVideo(video.video_id)}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {!expanded && selectedVideos.length > 0 && (
        <div className="flex flex-wrap gap-2 pt-1">
          {selectedVideos.map((video) => (
            <div
              key={video.video_id}
              className="flex items-center gap-2 rounded-md border border-border bg-muted/50 px-3 py-1.5 text-sm"
            >
              <Video className="h-3.5 w-3.5 shrink-0 text-purple-500" />
              <span className="max-w-[180px] truncate">{video.title || video.filename}</span>
              <button
                type="button"
                onClick={() => toggleVideo(video.video_id)}
                disabled={disabled}
                className="ml-auto text-muted-foreground hover:text-foreground disabled:opacity-50"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const KnowledgeAssetSelector: React.FC<{
  orgId: string;
  selectedAssetIds: string[];
  onSelectionChange: (ids: string[]) => void;
  disabled?: boolean;
  excludedAssetIds?: string[];
}> = ({
  orgId,
  selectedAssetIds,
  onSelectionChange,
  disabled = false,
  excludedAssetIds = [],
}) => {
  const [expanded, setExpanded] = useState(false);
  const [activeFilter, setActiveFilter] = useState<'all' | 'documents' | 'video' | 'url'>('all');
  const { assets, isLoading } = useKnowledgeAssets(orgId);

  const completedAssets = useMemo(
    () =>
      assets.filter(
        (asset) =>
          asset.processing_status === 'completed' && !excludedAssetIds.includes(asset.id)
      ),
    [assets, excludedAssetIds]
  );
  const filteredAssets =
    activeFilter === 'all'
      ? completedAssets
      : activeFilter === 'documents'
        ? completedAssets.filter((asset) => isDocumentKnowledgeAssetType(asset.asset_type))
        : completedAssets.filter((asset) => asset.asset_type === activeFilter);
  const selectedAssets = completedAssets.filter((asset) => selectedAssetIds.includes(asset.id));

  const toggleAsset = (assetId: string) => {
    if (selectedAssetIds.includes(assetId)) {
      onSelectionChange(selectedAssetIds.filter((id) => id !== assetId));
    } else {
      onSelectionChange([...selectedAssetIds, assetId]);
    }
  };

  return (
    <div className="space-y-2">
      <Label className="flex items-center gap-1.5">
        <BookOpen className="h-3.5 w-3.5" />
        Knowledge Assets
      </Label>

      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        disabled={disabled}
        className={cn(
          'flex w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background',
          'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50'
        )}
      >
        <span className="text-muted-foreground">
          {selectedAssetIds.length > 0
            ? `${selectedAssetIds.length} asset${selectedAssetIds.length > 1 ? 's' : ''} selected`
            : 'Select knowledge assets...'}
        </span>
        <ChevronDown
          className={cn('h-4 w-4 opacity-50 transition-transform', expanded && 'rotate-180')}
        />
      </button>

      {expanded && (
        <div className="space-y-2 pt-1">
          <div className="flex gap-1 rounded-lg bg-muted p-1">
            {([
              { key: 'all', label: 'All' },
              { key: 'documents', label: 'Documents' },
              { key: 'video', label: 'Videos' },
              { key: 'url', label: 'URLs' },
            ] as const).map(({ key, label }) => {
              const count =
                key === 'all'
                  ? selectedAssetIds.length
                  : key === 'documents'
                    ? completedAssets.filter(
                        (asset) =>
                          isDocumentKnowledgeAssetType(asset.asset_type) &&
                          selectedAssetIds.includes(asset.id)
                      ).length
                    : completedAssets.filter(
                        (asset) =>
                          asset.asset_type === key && selectedAssetIds.includes(asset.id)
                      ).length;

              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setActiveFilter(key)}
                  className={cn(
                    'flex-1 rounded-md px-2.5 py-1 text-xs font-medium transition-colors',
                    activeFilter === key
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  )}
                >
                  {label}
                  {count > 0 && (
                    <span className="ml-1 inline-flex items-center justify-center rounded-full bg-primary/10 px-1.5 text-[10px] font-semibold text-primary">
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          ) : completedAssets.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              No knowledge assets available. Upload documents on the Background page.
            </p>
          ) : filteredAssets.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              No {activeFilter} assets available.
            </p>
          ) : (
            <div className="max-h-60 space-y-1 overflow-y-auto rounded-md border border-input p-1">
              {filteredAssets.map((asset) => (
                <button
                  key={asset.id}
                  type="button"
                  onClick={() => toggleAsset(asset.id)}
                  disabled={disabled}
                  className={cn(
                    'flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm transition-colors',
                    selectedAssetIds.includes(asset.id)
                      ? 'bg-primary/10 text-primary'
                      : 'hover:bg-muted',
                    disabled && 'cursor-not-allowed opacity-50'
                  )}
                >
                  <Checkbox
                    checked={selectedAssetIds.includes(asset.id)}
                    tabIndex={-1}
                    className="pointer-events-none"
                  />
                  <AssetIcon
                    type={asset.asset_type}
                    className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
                  />
                  <span className="flex-1 truncate">
                    {asset.title || asset.filename || asset.url || 'Untitled'}
                  </span>
                  <span className="shrink-0 text-xs uppercase text-muted-foreground">
                    {getKnowledgeAssetTypeLabel(asset.asset_type, asset.url_type)}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {!expanded && selectedAssets.length > 0 && (
        <div className="flex flex-wrap gap-2 pt-1">
          {selectedAssets.map((asset) => (
            <div
              key={asset.id}
              className="flex items-center gap-2 rounded-md border border-border bg-muted/50 px-3 py-1.5 text-sm"
            >
              <AssetIcon
                type={asset.asset_type}
                className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
              />
              <span className="max-w-[180px] truncate">
                {asset.title || asset.filename || asset.url || 'Untitled'}
              </span>
              <button
                type="button"
                onClick={() => toggleAsset(asset.id)}
                disabled={disabled}
                className="ml-auto text-muted-foreground hover:text-foreground disabled:opacity-50"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
